(() => {
  const CARD_SELECTOR = '.product-card-wrapper, .search-modal__product-card';
  const ACTION_SELECTOR = '.product-card__quick-add-button, .product-card__wishlist-button--overlay';
  const PRIMARY_IMAGE_SELECTOR = '.product-card__primary-image';
  const SECONDARY_IMAGE_SELECTOR = '.product-card__secondary-image';
  const SLIDER_SELECTOR = '[data-product-card-media-slider]';
  const SAMPLE_SIZE = 24;
  const LIGHT_ICON = '#ffffff';
  const DARK_ICON = '#171411';
  const LIGHT_SHADOW =
    'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.52)) drop-shadow(0 0 1px rgba(0, 0, 0, 0.3))';
  const DARK_SHADOW =
    'drop-shadow(0 1px 2px rgba(255, 255, 255, 0.66)) drop-shadow(0 0 1px rgba(255, 255, 255, 0.42))';

  const initializedCards = new WeakSet();
  const visibleCards = new Set();
  const mobileMediaQuery = window.matchMedia('(max-width: 749px)');
  let resizeFrame = 0;

  function toLinearChannel(channel) {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  }

  function getRelativeLuminance(red, green, blue) {
    return 0.2126 * toLinearChannel(red) + 0.7152 * toLinearChannel(green) + 0.0722 * toLinearChannel(blue);
  }

  function getImageSample(image, frame, action) {
    if (!image.complete || image.naturalWidth < 2 || image.naturalHeight < 2) return null;

    const frameRect = frame.getBoundingClientRect();
    const actionRect = action.getBoundingClientRect();
    if (!frameRect.width || !frameRect.height || !actionRect.width || !actionRect.height) return null;

    const scale = Math.max(frameRect.width / image.naturalWidth, frameRect.height / image.naturalHeight);
    const renderedWidth = image.naturalWidth * scale;
    const renderedHeight = image.naturalHeight * scale;
    const cropX = (renderedWidth - frameRect.width) / 2;
    const cropY = (renderedHeight - frameRect.height) / 2;
    const centreX = actionRect.left + actionRect.width / 2 - frameRect.left;
    const centreY = actionRect.top + actionRect.height / 2 - frameRect.top;
    const sampleWidth = Math.min(frameRect.width, Math.max(12, actionRect.width * 1.1));
    const sampleHeight = Math.min(frameRect.height, Math.max(12, actionRect.height * 1.1));
    const sampleLeft = Math.max(0, Math.min(frameRect.width - sampleWidth, centreX - sampleWidth / 2));
    const sampleTop = Math.max(0, Math.min(frameRect.height - sampleHeight, centreY - sampleHeight / 2));

    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE_SIZE;
    canvas.height = SAMPLE_SIZE;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    try {
      /* Draw the complete decoded image into the sample canvas using its
       * displayed object-fit geometry. Responsive srcset images can expose
       * density-adjusted naturalWidth/naturalHeight values; using those as raw
       * bitmap crop coordinates samples the wrong part of the decoded image. */
      const sampleScaleX = SAMPLE_SIZE / sampleWidth;
      const sampleScaleY = SAMPLE_SIZE / sampleHeight;
      context.drawImage(
        image,
        (-cropX - sampleLeft) * sampleScaleX,
        (-cropY - sampleTop) * sampleScaleY,
        renderedWidth * sampleScaleX,
        renderedHeight * sampleScaleY
      );
      return context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
    } catch (error) {
      return null;
    }
  }

  function getIconTreatment(sample) {
    if (!sample) return null;

    const luminances = [];

    for (let index = 0; index < sample.length; index += 4) {
      if (sample[index + 3] < 128) continue;
      luminances.push(getRelativeLuminance(sample[index], sample[index + 1], sample[index + 2]));
    }

    if (!luminances.length) return null;

    /* Median luminance is less likely than a plain average to be skewed by a
     * small jewellery highlight or hard shadow crossing the heart position. */
    luminances.sort((first, second) => first - second);
    const middle = Math.floor(luminances.length / 2);
    const backgroundLuminance =
      luminances.length % 2
        ? luminances[middle]
        : (luminances[middle - 1] + luminances[middle]) / 2;
    const darkIconLuminance = getRelativeLuminance(23, 20, 17);
    const whiteContrast = 1.05 / (backgroundLuminance + 0.05);
    const darkContrast = (backgroundLuminance + 0.05) / (darkIconLuminance + 0.05);
    const useLightIcon = whiteContrast >= darkContrast;

    return {
      color: useLightIcon ? LIGHT_ICON : DARK_ICON,
      shadow: useLightIcon ? LIGHT_SHADOW : DARK_SHADOW,
      tone: useLightIcon ? 'light' : 'dark',
    };
  }

  function applyTreatment(action, imageKey, treatment) {
    if (!treatment) return;
    action.style.setProperty(`--product-card-icon-${imageKey}`, treatment.color);
    action.style.setProperty(`--product-card-icon-shadow-${imageKey}`, treatment.shadow);
    action.dataset[`productCard${imageKey === 'primary' ? 'Primary' : 'Secondary'}IconTone`] = treatment.tone;
  }

  function analyseImage(card, image, imageKey) {
    const frame = card.querySelector('.product-card-media-frame');
    if (!frame || !image) return;

    card.querySelectorAll(ACTION_SELECTOR).forEach((action) => {
      applyTreatment(action, imageKey, getIconTreatment(getImageSample(image, frame, action)));
    });
  }

  function analyseCard(card) {
    const whiteTreatment = {
      color: LIGHT_ICON,
      shadow: LIGHT_SHADOW,
      tone: 'light',
    };

    card.querySelectorAll(ACTION_SELECTOR).forEach((action) => {
      applyTreatment(action, 'primary', whiteTreatment);
      applyTreatment(action, 'secondary', whiteTreatment);
    });
  }

  function syncDisplayedImage(card) {
    const slider = card.querySelector(SLIDER_SELECTOR);
    const secondaryImage = card.querySelector(SECONDARY_IMAGE_SELECTOR);
    const secondaryLoaded = slider?.classList.contains('is-secondary-media-loaded');
    const secondarySuppressed = secondaryImage?.style.opacity === '0';
    const mobileSecondaryActive = slider?.classList.contains('is-mobile-secondary-active');
    const desktopSecondaryActive =
      !mobileMediaQuery.matches &&
      secondaryLoaded &&
      !secondarySuppressed &&
      (card.dataset.productCardPointerWithin === 'true' || card.matches(':focus-within'));

    card.dataset.productCardIconImage = mobileSecondaryActive || desktopSecondaryActive ? 'secondary' : 'primary';
  }

  function scheduleCardAnalysis(card) {
    window.requestAnimationFrame(() => {
      if (!card.isConnected) return;
      analyseCard(card);
      syncDisplayedImage(card);
    });
  }

  function watchImage(card, image) {
    if (!image) return;

    const handleImageChange = () => scheduleCardAnalysis(card);
    image.addEventListener('load', handleImageChange);
    image.addEventListener('error', () => syncDisplayedImage(card));

    new MutationObserver(handleImageChange).observe(image, {
      attributes: true,
      attributeFilter: ['src', 'srcset', 'style'],
    });
  }

  function initializeCard(card) {
    if (initializedCards.has(card)) return;
    initializedCards.add(card);
    visibleCards.add(card);

    card.dataset.productCardIconImage = 'primary';
    watchImage(card, card.querySelector(PRIMARY_IMAGE_SELECTOR));
    watchImage(card, card.querySelector(SECONDARY_IMAGE_SELECTOR));

    const slider = card.querySelector(SLIDER_SELECTOR);
    if (slider) {
      new MutationObserver(() => {
        scheduleCardAnalysis(card);
      }).observe(slider, { attributes: true, attributeFilter: ['class'] });
    }

    card.addEventListener('pointerenter', () => {
      card.dataset.productCardPointerWithin = 'true';
      syncDisplayedImage(card);
    });
    card.addEventListener('pointerleave', () => {
      card.dataset.productCardPointerWithin = 'false';
      syncDisplayedImage(card);
    });
    card.addEventListener('focusin', () => syncDisplayedImage(card));
    card.addEventListener('focusout', () => window.requestAnimationFrame(() => syncDisplayedImage(card)));
    scheduleCardAnalysis(card);
  }

  const proximityObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              proximityObserver.unobserve(entry.target);
              initializeCard(entry.target);
            });
          },
          { rootMargin: '700px 300px' }
        )
      : null;

  function hydrate(root = document) {
    root.querySelectorAll?.(CARD_SELECTOR).forEach((card) => {
      if (initializedCards.has(card)) return;
      if (proximityObserver) proximityObserver.observe(card);
      else initializeCard(card);
    });
  }

  function refreshCard(card) {
    const productCard = card?.matches?.(CARD_SELECTOR) ? card : card?.closest?.(CARD_SELECTOR);
    if (!productCard) return;
    initializeCard(productCard);
    scheduleCardAnalysis(productCard);
  }

  mobileMediaQuery.addEventListener('change', () => {
    visibleCards.forEach((card) => syncDisplayedImage(card));
  });

  window.addEventListener(
    'resize',
    () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => visibleCards.forEach(analyseCard));
    },
    { passive: true }
  );

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(CARD_SELECTOR)) hydrate(node.parentElement || document);
        else if (node.querySelector(CARD_SELECTOR)) hydrate(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => hydrate(event.target));

  window.ValoraieAdaptiveProductCardIcons = { hydrate, refreshCard };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => hydrate(), { once: true });
  else hydrate();
})();
